/**
 * Shared presentation helpers for EventListRow / EventCardTile / calendar
 * components. Each helper is pure — translation, status resolution, avatar
 * stack shaping, etc.
 */
import { resolveUserAvatarUrl, resolveUserDisplayName, resolveUserUsername } from './userIdentity';

const EVENT_STATUS_TO_CHIP_VARIANT = {
    DRAFT: 'pending',
    PUBLISHED: 'publish',
    COMPLETED: 'complete',
    CANCELLED: 'cancel',
};

const VIEWER_STATUS_TO_CHIP_VARIANT = {
    GOING: 'going',
    INTERESTED: 'interested',
    PENDING: 'pending',
    DECLINED: 'decline',
    LEFT: 'cancel',
};

export function mapEventToStatusVariant(status) {
    const normalized = typeof status === 'string' ? status.trim().toUpperCase() : '';
    return EVENT_STATUS_TO_CHIP_VARIANT[normalized] || 'publish';
}

export function mapViewerStatusToVariant(status) {
    const normalized = typeof status === 'string' ? status.trim().toUpperCase() : '';
    return VIEWER_STATUS_TO_CHIP_VARIANT[normalized] || 'interested';
}

export function resolveStatusLabel(t, status) {
    const normalized = typeof status === 'string' ? status.trim().toLowerCase() : 'unknown';
    return t(`home.postCard.relatedObject.status.${normalized || 'unknown'}`);
}

export function resolveViewerStatusLabel(t, status) {
    const normalized = typeof status === 'string' ? status.trim().toLowerCase() : '';
    if (!normalized) {
        return '';
    }
    return t(`home.postCard.relatedObject.viewerStatus.${normalized}`);
}

export function resolveJoinPolicyLabel(t, joinPolicy) {
    const normalized = typeof joinPolicy === 'string' ? joinPolicy.trim().toUpperCase() : '';

    if (normalized === 'APPROVAL_REQUIRED') {
        return t('home.postCard.relatedObject.joinPolicy.approvalRequired');
    }
    if (normalized === 'CLOSED') {
        return t('home.postCard.relatedObject.joinPolicy.closed');
    }
    if (normalized === 'OPEN') {
        return t('home.postCard.relatedObject.joinPolicy.open');
    }
    return '';
}

export function buildLocationLabel(location) {
    if (!location || typeof location !== 'object') {
        return '';
    }
    const parts = [
        location.locationName,
        location.city,
        location.stateOrRegion,
        location.country,
    ].filter(Boolean);
    return parts.join(' · ');
}

export function buildShortLocationLabel(location) {
    if (!location || typeof location !== 'object') {
        return '';
    }
    const parts = [
        location.locationName || location.city,
        location.city && location.locationName ? location.city : null,
    ].filter(Boolean);
    return parts.join(' · ');
}

function readText(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function readImageUrl(source) {
    if (!source || typeof source !== 'object') {
        return '';
    }

    return readText(source.profilePicture)
        || readText(source.avatarUrl)
        || readText(source.imageUrl)
        || readText(source.avatar?.url)
        || readText(source.images?.avatar?.url);
}

export function resolveEventHostPresentation(t, item, options = {}) {
    const unknownKey = readText(options.unknownKey) || 'eventsPage.card.hostUnknown';
    const organizerType = readText(item?.organizer?.objectType).toUpperCase();
    if (organizerType === 'BAND') {
        return {
            displayName: readText(item?.organizer?.name)
                || readText(item?.organizer?.displayName)
                || t(unknownKey),
            avatarUrl: readImageUrl(item?.organizer),
            objectType: 'BAND',
            objectId: readText(item?.organizer?.objectId) || null,
        };
    }

    return {
        displayName: resolveUserDisplayName(item?.author, t(unknownKey)),
        avatarUrl: resolveUserAvatarUrl(item?.author) || readImageUrl(item?.author),
        objectType: 'USER',
        objectId: readText(item?.author?.id) || readText(item?.ownerId) || null,
    };
}

export function resolveEventHostLabel(t, item) {
    return resolveEventHostPresentation(t, item).displayName;
}

function normalizeAttendanceUser(user) {
    if (!user || typeof user !== 'object') {
        return null;
    }

    const id = user.id ?? user.userId ?? user.objectId ?? null;
    const displayName = resolveUserDisplayName(user);
    const username = resolveUserUsername(user);
    const avatarUrl = resolveUserAvatarUrl(user) ?? undefined;

    if (!id && !displayName && !username) {
        return null;
    }

    return {
        id,
        displayName,
        username,
        avatarUrl,
    };
}

/**
 * Build the real-user slice of an AvatarStack.
 *
 * The event timeline currently exposes only summary counts, not attendee
 * identities, so we render avatars only when a caller explicitly provides real
 * attendee user objects. The host/author is not an attendee proxy.
 */
export function buildAttendanceUsers(item) {
    const rawUsers = Array.isArray(item?.attendanceUsers)
        ? item.attendanceUsers
        : (Array.isArray(item?.attendees) ? item.attendees : []);

    return rawUsers
        .map(normalizeAttendanceUser)
        .filter(Boolean);
}

/**
 * How many more "going" attendees exist beyond the ones we render as real
 * avatars. Used to drive AvatarStack's overflow prop.
 */
export function attendanceOverflowCount(item) {
    if (!item?.attendanceSummary) {
        return 0;
    }
    const going = Number.isFinite(item.attendanceSummary.going) ? item.attendanceSummary.going : 0;
    const visible = buildAttendanceUsers(item).length;
    return Math.max(0, going - visible);
}

export function buildAttendanceSummaryLabel(t, summary) {
    const going = summary?.going ?? 0;
    const interested = summary?.interested ?? 0;
    const pending = summary?.pending ?? 0;
    return t('home.postCard.relatedObject.attendanceSummary', {
        going,
        interested,
        pending,
    });
}

/**
 * Short "Fri · 7:00 PM" style label for card / row meta pills.
 * Uses Intl.DateTimeFormat directly to bypass the shared date-formatter hook's
 * hardcoded year/month/day defaults (we want only weekday + time).
 */
export function formatShortScheduledLabel(locale, startAt, timezone) {
    if (!startAt) return '';
    const date = startAt instanceof Date ? startAt : new Date(startAt);
    if (Number.isNaN(date.getTime())) return '';
    try {
        const weekday = new Intl.DateTimeFormat(locale, {
            weekday: 'short',
            timeZone: timezone || undefined,
        }).format(date);
        const time = new Intl.DateTimeFormat(locale, {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: timezone || undefined,
        }).format(date);
        return `${weekday} · ${time}`;
    } catch (error) {
        return '';
    }
}
